import { cityById } from "./cities.ts";
import { LAKH } from "./types.ts";
import type { PlanInputs } from "./types.ts";

/**
 * Every default here is sourced. Where a number is a judgement call rather than
 * an observation, the comment says so. Users can change all of them.
 */
export const DEFAULTS: PlanInputs = {
  household: {
    adultAge: 45,
    childAges: [9, 6],
    parentCount: 2,
    parentAge: 80,
  },

  spending: {
    // Core lifestyle only — schooling, elder care, health cover and tax are separate.
    lifestylePerYear: 60 * LAKH,
    lifestylePricedLocally: false,
    // All-in: fees plus transport, activities, tutoring. Premium Bengaluru
    // international schools headline Rs 6.5-9.5 L/yr.
    schoolPerChildPerYear: 9 * LAKH,
    // US private university, all-in, per child per year.
    undergradPerYear: 75 * LAKH,
    undergradYears: 4,
    undergradStartAge: 18,
    healthCoverPerYear: 84_000,
    // Bengaluru market rates: live-in attendant Rs 20-25k/mo, day attendant
    // Rs 1,100-2,000/day, nurse Rs 35-50k/mo. Quoted for two parents.
    elderRamp: [
      { fromAge: 0, perYear: 25_000 * 12 },
      { fromAge: 84, perYear: 70_000 * 12 },
      { fromAge: 88, perYear: 140_000 * 12 },
    ],
    elderEndAge: 93,
    cityIndex: 1.0,
    localShare: 0.7,
    cityId: "bengaluru",
    remoteAccessPerYear: 0,
  },

  inflation: {
    // July 2026 CPI was 4.45%; RBI targets 4% +/- 2. Six percent is the
    // planning rate, deliberately above spot.
    general: 0.06,
    generalVol: 0.02,
    equityCorrelation: -0.25,
    // A fat basket - services, domestic help, private healthcare, travel -
    // runs hotter than headline CPI.
    life: 0.065,
    school: 0.09,
    // Overseas tuition ~4-5% plus rupee depreciation.
    undergrad: 0.06,
    // Indian medical inflation is widely put at 10-14%.
    medical: 0.11,
  },

  markets: {
    // Indian equity: Nifty 50 since-inception CAGR 10.59%; the 20-year rolling
    // CAGR fell below 10% in FY26. Global equity in INR: ~8.5% USD plus ~2.5%
    // rupee depreciation. Debt: 10-year G-sec was 6.96% on 4 Sep 2026.
    mu: [0.11, 0.11, 0.069, 0.08],
    sigma: [0.22, 0.17, 0.045, 0.15],
    correlation: [
      [1.0, 0.55, 0.05, 0.05],
      [0.55, 1.0, 0.0, 0.1],
      [0.05, 0.0, 1.0, 0.1],
      [0.05, 0.1, 0.1, 1.0],
    ],
    allocStart: [0.34, 0.26, 0.3, 0.1],
    allocEnd: [0.24, 0.18, 0.48, 0.1],
    glideYears: 25,
    annualTax: [0, 0, 0, 0],
    presetId: "glide",
  },

  // Equity LTCG is 12.5%, but a mature SWP is mostly return of capital, so the
  // drag on a gross withdrawal is far below the headline rate. Rises with the
  // spending tier as surcharge starts to bite.
  taxDrag: 0.115,

  horizonYears: 50,
  yearsToRetirement: 2,
  guardrails: true,
  simulations: 20_000,
  seed: 2028,
};

/** Tax drag rises with the spending tier. */
export function taxDragForSpend(lifestylePerYear: number): number {
  const lakhs = lifestylePerYear / LAKH;
  if (lakhs <= 40) return 0.095;
  if (lakhs <= 70) return 0.115;
  return 0.135;
}

/** Apply a city profile to a plan, moving every field the city governs. */
export function applyCity(spending: PlanInputs["spending"], cityId: string): PlanInputs["spending"] {
  const city = cityById(cityId);
  return {
    ...spending,
    cityId: city.id,
    cityIndex: city.index,
    remoteAccessPerYear: city.remoteAccessPerYear,
  };
}


/**
 * How the corpus is invested — and, just as importantly, how it is taxed.
 *
 * Two assets with the same headline return are not equivalent. A fixed deposit
 * is taxed at slab every year on the whole of its interest, spent or not. A
 * debt fund holding the same paper is taxed only on the gain in what you
 * actually withdraw, so the untouched part keeps compounding. Over fifty years
 * that difference is worth more than the choice between debt and equity.
 */
export interface AllocationPreset {
  id: string;
  label: string;
  /** Indian equity, global equity, Indian debt, gold. */
  allocStart: [number, number, number, number];
  allocEnd: [number, number, number, number];
  glideYears: number;
  annualTax: [number, number, number, number];
  /** Tax on a withdrawal. Null means use the rate for the spending tier. */
  taxDrag: number | null;
  note: string;
}

export const ALLOCATION_PRESETS: AllocationPreset[] = [
  {
    id: "fd",
    label: "Bank fixed deposits only",
    allocStart: [0, 0, 1, 0], allocEnd: [0, 0, 1, 0], glideYears: 0,
    annualTax: [0, 0, 0.312, 0],
    taxDrag: 0.01,
    note: "Interest is taxed at slab every year, spent or not. That leaves about 4.75% against 6.5% lifestyle inflation — the corpus loses purchasing power while sitting there. Safest in nominal terms, by far the most expensive in real ones.",
  },
  {
    id: "debt-funds",
    label: "Debt funds & target maturity",
    allocStart: [0, 0, 1, 0], allocEnd: [0, 0, 1, 0], glideYears: 0,
    annualTax: [0, 0, 0, 0],
    taxDrag: 0.20,
    note: "The same underlying paper as an FD, but taxed only on the gain in what you withdraw. Everything you leave alone keeps compounding untaxed. Similar risk, materially better arithmetic.",
  },
  {
    id: "conservative",
    label: "Conservative — 30% equity",
    allocStart: [0.18, 0.12, 0.6, 0.1], allocEnd: [0.18, 0.12, 0.6, 0.1], glideYears: 0,
    annualTax: [0, 0, 0, 0], taxDrag: null,
    note: "Indian research finds 20–50% equity maximises the sustainable withdrawal rate: the lower volatility cuts sequence risk by more than the lower return costs.",
  },
  {
    id: "balanced",
    label: "Balanced — 50% equity",
    allocStart: [0.3, 0.2, 0.4, 0.1], allocEnd: [0.3, 0.2, 0.4, 0.1], glideYears: 0,
    annualTax: [0, 0, 0, 0], taxDrag: null,
    note: "Half in equity, split between India and global, with a tenth in gold.",
  },
  {
    id: "glide",
    label: "Gliding — 60% growth falling to 42%",
    allocStart: [0.34, 0.26, 0.3, 0.1], allocEnd: [0.24, 0.18, 0.48, 0.1], glideYears: 25,
    annualTax: [0, 0, 0, 0], taxDrag: null,
    note: "The plan's default: more growth early, when the horizon is long, de-risking over 25 years as it shortens.",
  },
  {
    id: "growth",
    label: "Growth — 75% equity",
    allocStart: [0.45, 0.3, 0.15, 0.1], allocEnd: [0.45, 0.3, 0.15, 0.1], glideYears: 0,
    annualTax: [0, 0, 0, 0], taxDrag: null,
    note: "Highest expected return and the deepest drawdowns. A bad first decade here is what the sequence-risk buffer exists for.",
  },
];

export function presetById(id: string): AllocationPreset {
  return ALLOCATION_PRESETS.find((p) => p.id === id) ?? ALLOCATION_PRESETS[4];
}

/** Apply a preset, moving the weights, the annual tax and the withdrawal drag together. */
export function applyAllocation(inputs: PlanInputs, id: string): PlanInputs {
  const a = presetById(id);
  return {
    ...inputs,
    markets: {
      ...inputs.markets,
      allocStart: a.allocStart,
      allocEnd: a.allocEnd,
      glideYears: a.glideYears,
      annualTax: a.annualTax,
      presetId: a.id,
    },
    taxDrag: a.taxDrag ?? taxDragForSpend(inputs.spending.lifestylePerYear),
  };
}
